/**
 * useImageRating Hook
 *
 * Mutation hook for submitting image ratings.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rateImage } from '@/lib/api/image-ratings';

export interface UseImageRatingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface GalleryImage {
  id: string;
  user_rating: number | null;
  average_rating?: number;
  rating_count?: number;
}

interface GalleryData {
  images: GalleryImage[];
}

export function useImageRating(
  imageId: string | null,
  options: UseImageRatingOptions = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ rating, comment }: { rating: number; comment?: string }) => {
      if (!imageId) throw new Error('No image ID');
      return rateImage(imageId, rating, comment);
    },
    onMutate: async ({ rating }) => {
      console.log('[Rating] onMutate - Optimistic update, rating:', rating, 'imageId:', imageId);

      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['gallery-v2', 'search'] });

      // Snapshot previous values for rollback (all matching queries!)
      const previousQueries = queryClient.getQueriesData({ queryKey: ['gallery-v2', 'search'] });

      // Optimistically update ALL gallery queries (with different params)
      queryClient.setQueriesData({ queryKey: ['gallery-v2', 'search'] }, (old: GalleryData | undefined) => {
        if (!old?.images) {
          console.log('[Rating] No images in cache, skipping');
          return old;
        }

        console.log('[Rating] Cache has', old.images.length, 'images');

        let found = false;
        const updatedImages = old.images.map((img) => {
          if (img.id === imageId) {
            console.log('[Rating] ✅ FOUND image in cache! Old rating:', img.user_rating, '→ New:', rating);
            found = true;
            return { ...img, user_rating: rating };
          }
          return img;
        });

        if (!found) {
          console.log('[Rating] ❌ Image NOT FOUND in cache! Looking for:', imageId);
          console.log('[Rating] Available IDs:', old.images.map((img) => img.id).slice(0, 5));
        }

        console.log('[Rating] Cache updated optimistically');
        return { ...old, images: updatedImages };
      });

      return { previousQueries };
    },
    onError: (error: Error, _variables, context: { previousQueries: [readonly unknown[], unknown][] } | undefined) => {
      console.error('[Rating] Error, rolling back:', error);
      // Rollback ALL queries to previous data on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      options.onError?.(error);
    },
    onSuccess: (data) => {
      console.log('[Rating] Success, updating with server response:', data);
      // Update ALL gallery queries with actual server response
      queryClient.setQueriesData({ queryKey: ['gallery-v2', 'search'] }, (old: GalleryData | undefined) => {
        if (!old?.images) return old;

        const updatedImages = old.images.map((img: GalleryImage) =>
          img.id === imageId
            ? {
                ...img,
                user_rating: data.rating,
                average_rating: data.average,
                rating_count: data.count,
              }
            : img
        );

        console.log('[Rating] Updated query with server data');
        return { ...old, images: updatedImages };
      });

      // Force invalidate to trigger re-render (React Query structural sharing workaround)
      console.log('[Rating] Invalidating queries to force UI update');
      queryClient.invalidateQueries({
        queryKey: ['gallery-v2', 'search'],
        refetchType: 'none' // Don't refetch, just mark as stale
      });

      options.onSuccess?.();
    },
  });
}
